import { useState } from 'react';
import { Check, Link2 } from 'lucide-react';

import { AppRoutes } from '@/constants/routes';
import { cn } from '@/utils/cn';

/**
 * "Copy share link" — the public address of a guide, ready to paste into an email.
 *
 * The address it produces is the /kb mirror, not the /help page the reader is looking at.
 * Sending someone the in-app URL is the failure this button exists to prevent: they follow it,
 * hit the login screen, and conclude the documentation is behind a wall.
 */

/** The public, sign-in-free URL of a guide — or of the knowledge base itself when slug is omitted. */
export const publicGuideUrl = (slug?: string): string =>
  window.location.origin
  + (slug ? AppRoutes.KB_PUBLIC_GUIDE.replace(':slug', slug) : AppRoutes.KB_PUBLIC);

/**
 * Copy, with a fallback.
 *
 * navigator.clipboard is unavailable on plain http from anything but localhost, which is exactly
 * how this app is reached on an office LAN. The textarea route is ugly and still works there.
 */
const copy = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }

  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
};

interface Props {
  /** Guide slug. Omit to share the knowledge base index. */
  slug?: string;
  label?: string;
  /** Icon-only, for a card corner where a full button would crowd the title. */
  compact?: boolean;
  className?: string;
}

export const ShareLinkButton = ({ slug, label = 'Copy share link', compact = false, className }: Props) => {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const url = publicGuideUrl(slug);

  const onClick = async (e: React.MouseEvent) => {
    // Inside a card that is itself a link, so the click must not also navigate.
    e.preventDefault();
    e.stopPropagation();

    const ok = await copy(url);
    setState(ok ? 'copied' : 'failed');
    // Long enough to read, short enough that the button is ready when they share the next one.
    window.setTimeout(() => setState('idle'), 2200);

    // Nothing was copied and there is no third mechanism — show the address so it can be
    // selected by hand rather than leaving the reader with a button that silently did nothing.
    if (!ok) window.prompt('Copy this link:', url);
  };

  if (compact) {
    return (
      <button type="button" onClick={onClick} title={`Copy public link — ${url}`}
        aria-label="Copy public share link"
        className={cn(
          'rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600',
          'dark:hover:bg-slate-700 dark:hover:text-brand-300',
          state === 'copied' && 'text-emerald-600 dark:text-emerald-400', className)}>
        {state === 'copied' ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
      </button>
    );
  }

  return (
    <button type="button" onClick={onClick} title={url}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
        state === 'copied'
          ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
          : 'border-slate-200 bg-white text-slate-600 hover:border-brand-300 hover:text-brand-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-brand-600',
        className)}>
      {state === 'copied' ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
      {state === 'copied' ? 'Link copied' : state === 'failed' ? 'Copy it manually' : label}
    </button>
  );
};
